import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { MenubarModule } from 'primeng/menubar';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { RippleModule } from 'primeng/ripple';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { AuthRoles } from '../../../models/enums/enums';
import { MenuStateModel } from '../../../states/menu/menu-state-model';
import { UserSelectors } from '../../../states/user/user-selector';
import { UserStateModel } from '../../../states/user/user-state-model';
import { NotificationComponent } from '../../../notification/notification.component';
import { NotificationFactoryService } from '../../../services/notification/notification-factory.service';
import { ChatStateService } from '../../../services/chat/chat-state.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-partner-portal-top-nav-bar',
  standalone: true,
  imports: [
    MenubarModule,
    RouterModule,
    BadgeModule,
    AvatarModule,
    InputTextModule,
    RippleModule,
    CommonModule,
    MenuModule,
    ButtonModule,
    OverlayPanelModule,
    TieredMenuModule,
    NotificationComponent
  ],
  templateUrl: './top-nav-bar.component.html',
  styleUrl: './top-nav-bar.component.scss'
})
export class PartnerPortalTopNavBarComponent implements OnInit {
  items: MenuItem[] | undefined;
  userMenu: MenuItem[] | undefined;
  user?: UserStateModel;
  unreadNotificationCount: number = 0;
  chatUnreadCount$!: Observable<number>;
  notificationService: any;


  constructor(
    private router: Router,
    private store: Store,
    private chatStateService: ChatStateService,
    private notificationFactoryService: NotificationFactoryService
  ) {
    this.chatUnreadCount$ = this.chatStateService.totalUnreadCount$;
  }

  ngOnInit(): void {
    this.userMenu = [
      {
        label: 'Options',
        items: [
          {
            label: 'Account Centre',
            icon: 'pi pi-cog',
            command: () => {
              this.router.navigate([this.getAccountCentrePath()]);
            }
          },
          {
            label: 'Logout',
            icon: 'pi pi-sign-out',
            command: () => this.logout()
          }
        ]
      }
    ];

    this.store.select(UserSelectors.getUser).subscribe((user) => {
      this.user = user;
      this.applyFallbackIfNeeded();
    });

    this.store.select((s: { menu: MenuStateModel }) => s.menu).subscribe((m) => {
      if (m?.loaded && m.barItems?.length) {
        this.items = [...m.barItems];
        this.applyFallbackIfNeeded();
        return;
      }
      if (m?.loaded) {
        this.applyFallbackIfNeeded();
      }
    });
  }

  private applyFallbackIfNeeded(): void {
    if (!this.user?.role?.length) {
      return;
    }

    const isSuperAdmin = this.user.role.includes(AuthRoles.SuperAdmin);
    const isAdmin = this.user.role.includes(AuthRoles.Admin);
    const isMentor = this.user.role.includes(AuthRoles.Mentor);

    if (this.items?.length) {
      let updated = false;
      const newItems = [...this.items];

      if (isSuperAdmin && this.router.url.startsWith('/admin')) {
        const hasContactUs = newItems.some(i =>
          i.label === 'Contact Us Request' ||
          (i.items as MenuItem[])?.some(sub => sub.label === 'Contact Us Request')
        );

        if (!hasContactUs) {
          newItems.push({ label: 'Contact Us Request', routerLink: '/admin/contact-us-requests' });
          updated = true;
        }
      }


      if (updated) {
        this.items = newItems;
      }
      return;
    }

    if (this.user.role.includes(AuthRoles.SuperAdmin)) {
      this.items = [
        { label: 'Colleges', routerLink: '/admin/colleges' },
        {
          label: 'Mentor',
          items: [
            { label: 'Requests', routerLink: '/admin/mentors' },
            { label: 'Meetings', routerLink: '/admin/mentor-meetings' }
          ]
        },
        {
          label: 'Admin',
          items: [
            { label: 'School Manager', routerLink: '/admin/add-school' },
            { label: 'Admin Manager', routerLink: '/admin/admin-management' },
            { label: 'Student Manager', routerLink: '/admin/students' },
            { label: 'Integrations', routerLink: '/admin/integrations' },
            { label: 'Dynamic Forms', routerLink: '/admin/dynamic-forms' }
          ]
        },
        {
          label: 'Peer',
          items: [
            { label: 'Peer List', routerLink: '/admin/peer-list' },
            { label: 'Scheduled Calls', routerLink: '/admin/scheduled-peer-calls' }
          ]
        },
        {
          label: 'Mindler',
          items: [
            { label: 'Requests', routerLink: '/admin/mindler-tests' },
            { label: 'Reports', routerLink: '/admin/reporting' }
          ]
        },
        { label: 'Scholarships', routerLink: '/admin/add-scholarships' },
        { label: 'Student-Mentor Requests', routerLink: '/admin/student-mentor-requests' },
        { label: 'Contact Us', routerLink: '/admin/contact-us-requests' },
        {
          label: 'Payments',
          items: [
            { label: 'Packages', routerLink: '/admin/packages' },
            { label: 'Promo Codes', routerLink: '/admin/promocodes' },
            { label: 'Reports', routerLink: '/admin/payment-reports' }
          ]
        }
      ];
      return;
    }

    if (this.user.role.includes(AuthRoles.Admin)) {
      this.items = [
        { label: 'Student Manager', routerLink: '/admin/students' },
        {
          label: 'Mindler',
          items: [
            { label: 'Requests', routerLink: '/admin/mindler-tests' },
            { label: 'Reports', routerLink: '/admin/reporting' }
          ]
        },
        {
          label: 'Requests',
          items: [
            { label: 'Student-Mentor Requests', routerLink: '/admin/student-mentor-requests' }
          ]
        },
        { label: 'Admin Manager', routerLink: '/admin/admin-management' },
        {
          label: 'Peer',
          items: [
            { label: 'Peer Manager', routerLink: '/admin/peer-management' },
            { label: 'Peer List', routerLink: '/admin/peer-list' },
            { label: 'Scheduled Peer Calls', routerLink: '/admin/scheduled-peer-calls' }
          ]
        },
        { label: 'Logins', routerLink: '/admin/partner-logins' },
        {
          label: 'Payments',
          items: [
            { label: 'Reports', routerLink: '/admin/payment-reports' }
          ]
        }
      ];
      return;
    }

    if (this.user.role.includes(AuthRoles.SchoolAdmin)) {
      this.items = [
        { label: 'Students', routerLink: '/school-portal/students' },
        { label: 'Reporting', routerLink: '/school-portal/reporting' }
      ];
      return;
    }

    if (this.user.role.includes(AuthRoles.Mentor)) {
      this.items = [
        { label: 'Meetings', routerLink: '/mentor-portal/schedule-meetings' },
        { label: 'Dashboard', routerLink: '/mentor-portal/dashboard' }
      ];
      return;
    }

    if (this.user.role.includes(AuthRoles.Operations)) {
      this.items = [
        { label: 'Dashboard', routerLink: '/operations-portal/dashboard' },
        { label: 'Manage Students', routerLink: '/operations-portal/students' },
        { label: 'Requests', routerLink: '/operations-portal/requests' }
      ];
      return;
    }

    if (this.user.role.includes(AuthRoles.PeerReviewer)) {
      this.items = [
        { label: 'Timings', routerLink: '/peer-portal/timings' },
        { label: 'Scheduled Calls', routerLink: '/peer-portal/peer-meetings' }
      ];
    }
  }

  private getAccountCentrePath(): string {
    const url = this.router.url;
    if (url.startsWith('/admin')) {
      return '/admin/account-centre';
    }
    if (url.startsWith('/mentor-portal')) {
      return '/mentor-portal/account-centre';
    }
    if (url.startsWith('/school-portal')) {
      return '/school-portal/account-centre';
    }
    if (url.startsWith('/peer-portal')) {
      return '/peer-portal/account-centre';
    }
    if (url.startsWith('/operations-portal')) {
      return '/operations-portal/account-centre';
    }
    // fallback
    return '/mentor-portal/account-centre';
  }


  fetchUnreadNotificationCount() {
    this.notificationFactoryService.getUnreadNotificationCount().subscribe({
      next: (response) => {
        this.unreadNotificationCount = response || 0;
      },
      error: () => {
        this.unreadNotificationCount = 0;
      }
    });
  }

  markAllAsRead() {
    this.notificationFactoryService.markAllAsRead().subscribe({
      next: () => {
        this.unreadNotificationCount = 0;
      }
    });
  }
  private getChatPath(): string {
    const url = this.router.url;
    if (url.startsWith('/admin')) {
      return '/admin/chat';
    }
    if (url.startsWith('/mentor-portal')) {
      return '/mentor-portal/chat';
    }
    if (url.startsWith('/school-portal')) {
      return '/school-portal/chat';
    }
    if (url.startsWith('/peer-portal')) {
      return '/peer-portal/chat';
    }
    if (url.startsWith('/operations-portal')) {
      return '/operations-portal/chat';
    }
    // fallback
    return '/mentor-portal/chat';
  }

  onChatIconClick(): void {
    const chatPath = this.getChatPath();
    if (this.router.url !== chatPath) {
      this.router.navigate([chatPath]);
    }
  }

  private logout(): void {
    localStorage.clear();
    this.router.navigate(['/partner-signin']);
  }
}
